import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import CheckboxTree from "react-checkbox-tree";
import "react-checkbox-tree/lib/react-checkbox-tree.css";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";

function RolePermissions() {
  const token = localStorage.getItem("token");

  // const [triggerRerender, setTriggerRerender] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
  } = useForm();

  const [checked, setChecked] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [roleData, setRoleData] = useState([]); // State for categories

  const getRoles = async () => {
    if (!token) return;
    try {
      const response = await get("Role", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoleData(response); // Assuming response.navCategory contains the roles
    } catch (error) {
      console.error("Error fetching Roles:", error);
    }
  };

  useEffect(() => {
    getRoles();
  }, []);

  useEffect(() => {
    if (selectedRoleId) {
      const fetchRolePermissions = async () => {
        try {
          const formData = { id: selectedRoleId };

          const response = await post(
            `UserAuthentication/GetRolePermission`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.isSuccess) {
            const { rolePermission } = response;

            // Transform the response into the format needed for CheckboxTree
            const transformedNodes = transformPermissions(rolePermission);
            setNodes(transformedNodes);
            const getCheckedValues = (nodes) => {
              console.log(nodes);
              return nodes.flatMap((node) => {
                const checkedChildren = getCheckedValues(node.children || []);
                return node.status
                  ? [node.value, ...checkedChildren]
                  : checkedChildren;
              });
            };

            const checkedValues = getCheckedValues(transformedNodes);
            setChecked(checkedValues);
          } else {
            console.error(
              "Failed to fetch role permissions:",
              response.message
            );
            setNodes([]); // Clear nodes if the fetch failed
          }
        } catch (error) {
          console.error("Error fetching role permissions:", error);
        }
      };

      fetchRolePermissions();
    } else {
      setNodes([]);
    }
  }, [selectedRoleId]);

  const transformPermissions = (permissionData) => {
    if (!permissionData || !permissionData.children) return [];
    return permissionData.children.map((category, catIndex) => ({
      value: `cat-${catIndex}-${category.value}`,
      label: category.label,
      status: category.status, // Default to false if status is undefined
      children:
        category.children?.map((subCategory, subCatIndex) => ({
          value: `subCat-${catIndex}-${subCatIndex}-${subCategory.value}`,
          label: subCategory.label,
          status: subCategory.status,
          children:
            subCategory.children?.map((form, formIndex) => ({
              value: `form-${catIndex}-${subCatIndex}-${formIndex}-${form.value}`,
              label: form.label,
              status: form.status,
              children:
                form.children?.map((permission, permIndex) => ({
                  value: `per-${catIndex}-${subCatIndex}-${formIndex}-${permIndex}-${permission.value}`,
                  label: permission.label,
                  status: permission.status,
                })) || [],
            })) || [],
        })) || [],
    }));
  };

  const saveData = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const transformedData = nodes.map((category) => ({
      Value: category.value,
      Label: category.label,
      Status: checked.includes(category.value) ? "true" : "false",
      SubCategories: category.children.map((subCategory) => ({
        Value: subCategory.value,
        Label: subCategory.label,
        Status: checked.includes(subCategory.value) ? "true" : "false",
        Forms: subCategory.children.map((form) => ({
          Value: form.value,
          Label: form.label,
          Status: checked.includes(form.value) ? "true" : "false",
          Permissions: form.children.map((permission) => ({
            Value: permission.value,
            Label: permission.label,
            Status: checked.includes(permission.value) ? "true" : "false",
          })),
        })),
      })),
    }));

    const formData = {
      roleId: selectedRoleId,
      category: transformedData,
    };

    try {
      const response = await post(
        `UserAuthentication/SaveRolePermission`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        // setTriggerRerender((prev) => !prev);
      }
    } catch (error) {
      console.error("Error saving permission:", error);
    }
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Role Permissions</h2>
      </div>

      <form className="row g-3" onSubmit={handleSubmit(saveData)}>
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-12">
                  <label className="form-label">Role</label>
                  <select
                    id="categorySelect"
                    value={selectedRoleId}
                    className="form-select"
                    onChange={(e) => setSelectedRoleId(e.target.value)}
                  >
                    <option value="">Select</option>
                    {roleData.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-8">
          <div className="card">
            <div className="card-body">
              <div className="row g-3">
                {nodes.length > 0 ? (
                  <>
                    <div className="col-md-12">
                      <label className="form-label">Permissions</label>
                      <CheckboxTree
                        nodes={nodes}
                        checked={checked}
                        expanded={expanded}
                        onCheck={(checked) => setChecked(checked)}
                        onExpand={(expanded) => setExpanded(expanded)}
                        icons={{
                          // check: <span className="rct-icon rct-icon-check" />, // Custom check icon
                          // uncheck: (
                          //   <span className="rct-icon rct-icon-uncheck" />
                          // ), // Custom uncheck icon
                          // halfCheck: (
                          //   <span className="rct-icon rct-icon-half-check" />
                          // ), // Custom half-check icon
                          // expandClose: null, // Remove folder icon for collapsed state
                          // expandOpen: null, // Remove folder icon for expanded state
                          // expandAll: null, // Optionally, remove expand all icon
                          // collapseAll: null, // Optionally, remove collapse all icon
                          parentClose: null, // Remove parent folder close icon
                          parentOpen: null, // Remove parent folder open icon
                          leaf: null, // Custom leaf icon
                        }}
                        iconsClass="fa5" // Keep any additional icon class, e.g., FontAwesome 5
                      />
                    </div>
                    <div className="col-12">
                      <button
                        className="btn btn-primary btn-sm"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Save" : "Save"}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-center mt-3">
                    <p>There are no records to display</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}

export default RolePermissions;
